import {createAsyncThunk, createSelector, createSlice} from '@reduxjs/toolkit';
import {AsyncState, RejectedState} from '../../models/async_state';
import {Status} from '../../enums/status';
import {GetChatQuery} from '../../gql/customer/graphql';
import {Customer} from '../../network/customer';
import {RootState} from '../store';

interface State extends GetChatQuery {}

export const fetchChat = createAsyncThunk('chat/fetchChat', async (data: {token: string}) => {
    const result = await Customer.chat(data.token);
    return result.data;
});
export const fetchUnreadChat = createAsyncThunk(
    'chat/fetchUnreadChat',
    async (data: {token: string; lastMessageId?: number}) => {
        const result = await Customer.chat(data.token, false, data.lastMessageId);
        return result.data;
    },
);
export const sendChatMessage = createAsyncThunk(
    'chat/sendChatMessage',
    async (data: {
        token: string;
        message: string;
        url?: string;
        persistedNotificationId?: number;
        lastMessageId?: number;
        file?: File | FormDataEntryValue | null;
    }) => {
        const result = await Customer.sendChatMessage(
            data.token,
            data.message,
            data.url,
            data.persistedNotificationId,
            data.lastMessageId,
            data.file,
        );
        return result.data;
    },
);
export const markMessagesRead = createAsyncThunk('chat/markMessagesRead', async (data: {token: string}) => {
    const result = await Customer.markMessagesRead(data.token);
    return result.data;
});

export const unreadMessagesCountSelector = (state: RootState) => {
    return unreadMessagesSelector(state).length;
};

export const unreadMessagesSelector = createSelector(
    (state: RootState) => state.chat.chat?.messages,
    (messages) => {
        return (messages ?? []).filter((msg) => msg?.read === false && msg.user !== null);
    },
);

function mergedMessages(
    oldMessages?: NonNullable<GetChatQuery['chat']>['messages'],
    newMessages?: NonNullable<GetChatQuery['chat']>['messages'],
): NonNullable<GetChatQuery['chat']>['messages'] {
    const messagesMap = new Map(oldMessages?.map((msg) => [msg?.id, msg]));
    if (newMessages) {
        for (const message of newMessages) {
            if (message) {
                messagesMap.set(message.id, message);
            }
        }
    }
    return Array.from(messagesMap.values());
}

export const chatSlice = createSlice({
    name: 'chat',
    initialState: () => {
        const initialState: AsyncState<State> = {
            status: Status.IDLE,
        };
        return initialState as AsyncState<State>;
    },
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchChat.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(fetchChat.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            state.chat = action.payload.chat;
            state.__typename = action.payload.__typename;
        });
        builder.addCase(fetchChat.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });

        builder.addCase(fetchUnreadChat.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(fetchUnreadChat.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;

            state.chat = {
                ...action.payload.chat,
                messages: mergedMessages(state.chat?.messages, action.payload.chat?.messages),
                __typename: 'ChatType',
            };
            state.__typename = action.payload.__typename;
        });
        builder.addCase(fetchUnreadChat.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });

        builder.addCase(markMessagesRead.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(markMessagesRead.fulfilled, (state) => {
            state.status = Status.FULFILLED;
            state.chat = state.chat = {
                __typename: 'ChatType',
                ...state.chat,
                messages: state.chat?.messages?.map((msg) => ({...msg, __typename: 'ChatMessageType', read: true})),
            };
        });
        builder.addCase(markMessagesRead.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });

        builder.addCase(sendChatMessage.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(sendChatMessage.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            if (action.payload?.SendChatMessage) {
                state.chat = action.payload?.SendChatMessage;
            }
        });
        builder.addCase(sendChatMessage.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });
    },
});
